import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { setLoading } from "../../redux/loaderAction";
import Loader from "../../components/loader/Loader";
import SaveButton from "../../components/buttons/SaveButton";
import CancelButton from "../../components/buttons/CancelButton";
import Swal from "../../messageBox/customSweetAlert";
import { currentDateFormat } from "../../utils";
import {
  UPDATE_BILL_RATE_CONFIGURATIONV1,
  GET_SERVICE_NAMES,
  GET_ALL_PAYER,
  GET_LOCATION_TYPE,
  GET_STATE_URI,
} from "../../constant";
import { billRateValidation } from "../../validation/Validation";

/** Format Date as MM/DD/YYYY (zero-padded) for v1 API */
function formatDateMMDDYYYY(date) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

function formatCodeWithModifier(code, modifier) {
  const codeStr = code ?? "";
  const mod = modifier ?? "";
  if (mod !== "" && String(mod).trim() !== "") {
    return `${codeStr} - ${String(mod).trim()}`;
  }
  return codeStr;
}

const API_FIELD_TO_FORM_FIELD = {
  start_date: "effective_date",
  end_date: "end_date",
};

function mapApiDetailsToErrors(details) {
  if (!Array.isArray(details)) return {};
  return details.reduce((acc, item) => {
    const apiField = item?.field;
    const formField = API_FIELD_TO_FORM_FIELD[apiField] || apiField;
    if (formField && item?.message) {
      acc[formField] = item.message;
    }
    return acc;
  }, {});
}

/** Classify v1 update API result: 200 OK | 409 | 400 | 500 */
function parseBillRateUpdateV1ApiResult(httpStatus, body) {
  const status = httpStatus != null ? Number(httpStatus) : null;
  const message =
    body?.message ||
    "Failed to update bill rate configuration. Please try again.";

  if (
    status === 200 ||
    body?.status === 200 ||
    body?.status === "200"
  ) {
    if (body?.success !== false) {
      return {
        type: "success",
        message: message || "Bill Rate Configuration has been updated successfully.",
      };
    }
  }

  if (status === 409 || body?.code === 409) {
    return {
      type: "duplicate",
      message: message || "Bill rate configuration already exists",
    };
  }

  if (status === 400 || body?.status === 400 || body?.status === "400") {
    const validationMessage = body?.error
      ? `${body.error}: ${message}`
      : message;
    return {
      type: "validation",
      message: validationMessage || "Invalid data",
      details: body?.details,
    };
  }

  if (status === 500) {
    const serverMessage = body?.error
      ? `${body.error}: ${message}`
      : message;
    return {
      type: "server",
      message: serverMessage || "Internal server error. Please try again later.",
    };
  }

  if (body?.success === false && body?.code === 409) {
    return {
      type: "duplicate",
      message: message || "Bill rate configuration already exists",
    };
  }

  return { type: "error", message };
}

function handleBillRateUpdateV1ApiResult(httpStatus, body, handlers) {
  const result = parseBillRateUpdateV1ApiResult(httpStatus, body);
  switch (result.type) {
    case "success":
      handlers.onSuccess("Bill Rate Configuration has been updated successfully.");
      break;
    case "duplicate":
      handlers.onDuplicate(result.message);
      break;
    case "validation":
      handlers.onValidation(result.message, result.details);
      break;
    case "server":
      handlers.onServerError(result.message);
      break;
    default:
      handlers.onGenericError(result.message);
      break;
  }
}

function BillRateEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const loading = useSelector((state) => state.loader.loading);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Get bill rate data passed from BillRateList via navigation state
  const billRateData = location.state?.billRateData;
  console.log(billRateData);

  const [formData, setFormData] = useState({
    id: "",
    service_id: "",
    procedure_code: "",
    base_code: "",
    milage_code: "",
    state: "",
    payer_id: "",
    status: "1",
    base_bill_rate: "",
    mileage_bill_rate: "0.00",
    effective_date: "",
    end_date: "",
    notes: "",
  });

  const [effectiveDate, setEffectiveDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");

  const [states, setStateData] = useState([]);
  const [locationType, setLocationType] = useState([]);
  const [serviceNames, setServiceNames] = useState([]);
  const [serviceDetails, setServiceDetails] = useState(null);
  const [loadingServiceDetails, setLoadingServiceDetails] = useState(false);
  const [payerList, setPayerList] = useState([]);

  // Load dropdown data
  const loadDropdownData = async () => {
    try {
      const response1 = await axios.get(GET_STATE_URI);
      const response2 = await axios.get(GET_LOCATION_TYPE);
      const response3 = await axios.get(GET_SERVICE_NAMES, {
        params: { id: 2 },
      });
      const response4 = await axios.get(GET_ALL_PAYER);

      const statesData = response1.data || [];
      const servicesData = response3.data.data || [];
      const payersData = response4.data || [];

      setStateData(statesData);
      setLocationType(response2.data.data);
      setServiceNames(servicesData);
      setPayerList(payersData);

      return { services: servicesData, states: statesData, payers: payersData };
    } catch (error) {
      console.error("Error loading dropdown data:", error);
      return { services: [], states: [], payers: [] };
    }
  };

  // Initialize form with data passed from BillRateList
  const initializeFormData = (dropdownData) => {
    if (!billRateData) {
      // If no data passed, redirect back to list
      Swal.fire({
        title: "Error!",
        text: "No bill rate data found. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      }).then(() => {
        navigate("/home/billing/bill-rate-list", { replace: true });
      });
      return;
    }

    const data = billRateData;
    const { services, states, payers } = dropdownData;

    // Parse dates - using the field names from the API response
    let effDate = null;
    let eDate = null;

    if (data.start_date) {
      effDate = new Date(data.start_date);
      if (isNaN(effDate.getTime())) effDate = null;
    }

    if (data.end_date) {
      eDate = new Date(data.end_date);
      if (isNaN(eDate.getTime())) eDate = null;
    }

    setEffectiveDate(effDate);
    setEndDate(eDate);

    // Find the service_id by matching service_name
    const serviceName = data?.serviceDetails?.service_name;
    const matchedService = services.find(
      (service) =>
        service.service_name?.toLowerCase() === serviceName?.toLowerCase(),
    );
    const serviceId = matchedService?.id || data.service_id || "";

    // Find the payer_id by matching payer_name
    const payerName = data?.payerDetails?.payer_name;
    const matchedPayer = payers.find(
      (payer) => payer.payer_name?.toLowerCase() === payerName?.toLowerCase(),
    );
    const payerId = matchedPayer?.id || data.payer_id || "";

    // Find the state_id by matching state_name
    const stateName = data?.stateDetails?.state_name;
    const matchedState = states.find(
      (state) =>
        (state.state_name || state.name)?.toLowerCase() ===
        stateName?.toLowerCase(),
    );
    const stateId =
      matchedState?.state_id || matchedState?.id || data.state_id || "";

    const selectedService =
      matchedService || services.find((service) => service.id == serviceId);
    const serviceDetailsFromData = data?.serviceDetails;

    setFormData({
      id: data.id || id,
      service_id: serviceId,
      procedure_code: selectedService?.procedure_code || "",
      base_code: formatCodeWithModifier(
        serviceDetailsFromData?.base_code ?? selectedService?.base_code,
        serviceDetailsFromData?.modifier ?? selectedService?.modifier,
      ),
      milage_code: formatCodeWithModifier(
        serviceDetailsFromData?.milage_code ?? selectedService?.milage_code,
        serviceDetailsFromData?.milage_modifier ?? selectedService?.milage_modifier,
      ),
      state: stateId,
      payer_id: payerId,
      status: String(data?.bill_status ?? "1"),
      base_bill_rate: data.base_bill_rate || "",
      mileage_bill_rate: data.mileage_bill_rate || "0.00",
      effective_date: effDate ? formatDateMMDDYYYY(effDate) : "",
      end_date: eDate ? formatDateMMDDYYYY(eDate) : "",
      notes: data.notes || "",
    });

    if (selectedService) {
      setServiceDetails(selectedService);
    }

    setIsDataLoaded(true);
  };

  const fetchServiceDetails = async (serviceId) => {
    try {
      if (!serviceId) {
        setServiceDetails(null);
        setLoadingServiceDetails(false);
        return;
      }

      setLoadingServiceDetails(true);

      // Find the selected service from the already loaded serviceNames
      const selectedService = serviceNames.find(
        (service) => service.id == serviceId,
      );

      if (selectedService) {
        setServiceDetails(selectedService);
        // Auto-populate the form fields
        setFormData((prev) => ({
          ...prev,
          procedure_code: selectedService.procedure_code || "",
          base_code: formatCodeWithModifier(
            selectedService.base_code,
            selectedService.modifier,
          ),
          milage_code: formatCodeWithModifier(
            selectedService.milage_code,
            selectedService.milage_modifier,
          ),
        }));
      } else {
        setServiceDetails(null);
      }
    } catch (error) {
      console.error("Error fetching service details:", error);
      setServiceDetails(null);
    } finally {
      setLoadingServiceDetails(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      setCurrentDate(new Date());
      const dropdownData = await loadDropdownData();
      if (id && billRateData) {
        initializeFormData(dropdownData);
      } else if (!billRateData) {
        // No data passed, redirect to list
        navigate("/home/billing/bill-rate-list", { replace: true });
      }
    };
    initializeData();
  }, [id, billRateData]);

  // Fetch service details when service name changes (only after initial data load)
  useEffect(() => {
    if (isDataLoaded && formData.service_id && serviceNames.length > 0) {
      fetchServiceDetails(formData.service_id);
    }
  }, [formData.service_id, isDataLoaded, serviceNames]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setApiError("");
    setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const buildV1UpdatePayload = () => ({
    service_id: parseInt(formData.service_id, 10),
    payer_id: parseInt(formData.payer_id, 10),
    state_id: parseInt(formData.state, 10),
    base_bill_rate: parseFloat(formData.base_bill_rate),
    mileage_bill_rate: parseFloat(formData.mileage_bill_rate),
    bill_status: parseInt(formData.status, 10),
    start_date: formatDateMMDDYYYY(effectiveDate),
    end_date: formatDateMMDDYYYY(endDate),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    const validationData = {
      ...formData,
      effective_date: effectiveDate ? formatDateMMDDYYYY(effectiveDate) : "",
      end_date: endDate ? formatDateMMDDYYYY(endDate) : "",
    };

    const isValid = billRateValidation(validationData);
    setErrors(isValid);

    if (Object.keys(isValid).length > 0) {
      return;
    }

    const responseHandlers = {
      onSuccess: (message) => {
        Swal.fire({
          title: "Success!",
          text:
            message ||
            "Bill Rate Configuration has been updated successfully.",
          icon: "success",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        }).then(() => {
          navigate("/home/billing/bill-rate-list", { replace: true });
        });
      },
      onDuplicate: (message) => {
        Swal.fire({
          title: "Duplicate Configuration",
          text: message || "Bill rate configuration already exists",
          icon: "warning",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        });
      },
      onValidation: (message, details) => {
        const fieldErrors = mapApiDetailsToErrors(details);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldErrors }));
        }
        setApiError(message);
      },
      onServerError: (message) => {
        Swal.fire({
          title: "Server Error",
          text: message || "Internal server error. Please try again later.",
          icon: "error",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        });
      },
      onGenericError: (message) => {
        Swal.fire({
          title: "Error!",
          text: message,
          icon: "error",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        });
      },
    };

    try {
      dispatch(setLoading(true));
      const payload = buildV1UpdatePayload();
      const response = await axios.put(
        `${UPDATE_BILL_RATE_CONFIGURATIONV1}/${id}`,
        payload,
      );
      dispatch(setLoading(false));
      handleBillRateUpdateV1ApiResult(
        response.status,
        response.data,
        responseHandlers,
      );
    } catch (error) {
      dispatch(setLoading(false));
      const httpStatus = error?.response?.status;
      const body = error?.response?.data;

      if (httpStatus && body) {
        handleBillRateUpdateV1ApiResult(httpStatus, body, responseHandlers);
      } else {
        responseHandlers.onGenericError(
          error?.message ||
            "Failed to update bill rate configuration. Please try again.",
        );
      }
    }
  };

  const handleEffectiveDateChange = (date) => {
    setApiError("");
    setErrors((prev) => ({ ...prev, effective_date: null }));
    setEffectiveDate(date);
    setFormData((prev) => ({
      ...prev,
      effective_date: formatDateMMDDYYYY(date),
    }));
  };

  const handleEndDateChange = (date) => {
    setApiError("");
    setErrors((prev) => ({ ...prev, end_date: null }));
    setEndDate(date);
    setFormData((prev) => ({
      ...prev,
      end_date: formatDateMMDDYYYY(date),
    }));
  };

  return (
    <>
      <div
        className="tab-pane fade active show"
        id="edit-bill-rate"
        role="tabpanel"
        aria-labelledby="home-tab"
      >
        <section>
          <div className="d-flex justify-content-between form-container">
            <div>
              <h2 className="fs-5 font-weight-600">Edit Bill Rate</h2>
            </div>
            <div>
              <span>{currentDateFormat(currentDate)}</span>
            </div>
          </div>
        </section>

        <section>
          <div className="bg-white px-5 py-4">
            {apiError && (
              <div className="alert alert-danger mb-3" role="alert">
                {apiError}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              {/* Row 1 */}
              <div className="row mb-4">
                <div className="col-lg-4 col-md-6 mb-3">
                  <label className="require-field">Service Name</label>
                  <select
                    className={`form-select ${errors.service_id ? "is-invalid" : ""}`}
                    name="service_id"
                    value={formData?.service_id}
                    onChange={handleInputChange}
                    disabled={loadingServiceDetails}
                  >
                    <option value="">
                      {loadingServiceDetails
                        ? "Loading services..."
                        : "Select Service Name"}
                    </option>
                    {serviceNames.map((service, index) => (
                      <option key={index} value={service.id}>
                        {service.service_name}
                      </option>
                    ))}
                  </select>
                  {errors.service_id && (
                    <div className="errors">{errors.service_id}</div>
                  )}
                </div>

                <div className="col-lg-2 col-md-4 mb-3">
                  <label className="require-field">Base Code</label>
                  <div className="position-relative">
                    <input
                      type="text"
                      className={`form-control ${errors.base_code ? "is-invalid" : ""}`}
                      name="base_code"
                      value={formData.base_code}
                      onChange={handleInputChange}
                      placeholder={
                        loadingServiceDetails ? "Loading..." : "Enter base code"
                      }
                      autoComplete="off"
                      disabled={true}
                      style={{ backgroundColor: "#f8f9fa" }}
                    />
                    {loadingServiceDetails && formData.service_id && (
                      <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.base_code && (
                    <div className="invalid-feedback">{errors.base_code}</div>
                  )}
                </div>

                <div className="col-lg-2 col-md-4 mb-3">
                  <label className="require-field">Mileage Code</label>
                  <div className="position-relative">
                    <input
                      type="text"
                      className={`form-control ${errors.milage_code ? "is-invalid" : ""}`}
                      name="milage_code"
                      value={formData.milage_code}
                      onChange={handleInputChange}
                      placeholder={
                        loadingServiceDetails
                          ? "Loading..."
                          : "Enter mileage code"
                      }
                      autoComplete="off"
                      disabled={true}
                      style={{ backgroundColor: "#f8f9fa" }}
                    />
                    {loadingServiceDetails && formData.service_id && (
                      <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.milage_code && (
                    <div className="invalid-feedback">{errors.milage_code}</div>
                  )}
                </div>
              </div>

              {/* Row 2 */}
              <div className="row mb-4">
                <div className="col-lg-4 col-md-6 mb-3">
                  <label className="require-field">State</label>
                  <select
                    className={`form-select ${errors.state ? "is-invalid" : ""}`}
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                  >
                    <option value="">Select State</option>
                    {states.map((state, index) => (
                      <option key={index} value={state.state_id || state.id}>
                        {state.state_name || state.name}
                      </option>
                    ))}
                  </select>
                  {errors.state && <div className="errors">{errors.state}</div>}
                </div>

                <div className="col-lg-4 col-md-6 mb-3">
                  <label className="require-field">Payer Name</label>
                  <select
                    className={`form-select ${errors.payer_id ? "is-invalid" : ""}`}
                    name="payer_id"
                    value={formData.payer_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Payer</option>
                    {payerList.map((payer, index) => (
                      <option key={index} value={payer.id}>
                        {payer.payer_name}
                      </option>
                    ))}
                  </select>
                  {errors.payer_id && (
                    <div className="errors">{errors.payer_id}</div>
                  )}
                </div>

                <div className="col-lg-4 col-md-6 mb-3">
                  <label>Status</label>
                  <select
                    className="form-select"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Row 3 */}
              <div className="row mb-4">
                <div className="col-lg-4 col-md-6 mb-3">
                  <label className="require-field">Base Bill Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    className={`form-control ${errors.base_bill_rate ? "is-invalid" : ""}`}
                    name="base_bill_rate"
                    value={formData.base_bill_rate}
                    onChange={handleInputChange}
                    placeholder="Enter base bill rate"
                    autoComplete="off"
                  />
                  {errors.base_bill_rate && (
                    <div className="errors">{errors.base_bill_rate}</div>
                  )}
                </div>

                <div className="col-lg-4 col-md-6 mb-3">
                  <label className="require-field">Mileage Bill Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    className={`form-control ${errors.mileage_bill_rate ? "is-invalid" : ""}`}
                    name="mileage_bill_rate"
                    value={formData.mileage_bill_rate}
                    onChange={handleInputChange}
                    placeholder="Enter mileage bill rate"
                    autoComplete="off"
                  />
                  {errors.mileage_bill_rate && (
                    <div className="errors">{errors.mileage_bill_rate}</div>
                  )}
                </div>
              </div>

              {/* Row 4 */}
              <div className="row mb-4">
                <div className="col-lg-4 col-md-6 mb-3">
                  <div>
                    <label className="require-field d-block">
                      Effective Date
                    </label>
                    <DatePicker
                      selected={effectiveDate}
                      onChange={handleEffectiveDateChange}
                      dateFormat="MM/dd/yyyy"
                      className={`form-control ${errors.effective_date ? "is-invalid" : ""}`}
                      placeholderText="Select effective date"
                      isClearable
                    />
                  </div>
                  {errors.effective_date && (
                    <div className="errors">{errors.effective_date}</div>
                  )}
                </div>

                <div className="col-lg-4 col-md-6 mb-3">
                  <div>
                    <label className="require-field d-block">End Date</label>
                    <DatePicker
                      selected={endDate}
                      onChange={handleEndDateChange}
                      dateFormat="MM/dd/yyyy"
                      className={`form-control ${errors.end_date ? "is-invalid" : ""}`}
                      placeholderText="Select end date"
                      isClearable
                      minDate={effectiveDate || null}
                    />
                  </div>
                  {errors.end_date && (
                    <div className="errors">{errors.end_date}</div>
                  )}
                </div>
              </div>

              {/* Notes Section */}
              <div className="row mb-4">
                <div className="col-12">
                  <label className="">Notes</label>
                  <textarea
                    className="form-control"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Additional notes or comments"
                    rows="4"
                    style={{ resize: "both" }}
                  />
                  {errors.notes && <div className="errors">{errors.notes}</div>}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="footer-content">
                <SaveButton type="submit" buttonTitle={"Update Rate"} />
                <CancelButton
                  cancelHandler={() =>
                    navigate("/home/billing/bill-rate-list", { replace: true })
                  }
                />
              </div>
            </form>
          </div>
        </section>
      </div>

      <div>{loading && <Loader />}</div>
    </>
  );
}

export default BillRateEdit;
